import React, { useState, useEffect } from 'react';

function ProgressTracker({ studyGuide, studyTime }) {
  const [progress, setProgress] = useState({
    versesRead: 0,
    notesWritten: 0,
    questionsAnswered: 0,
    sectionsCompleted: 0
  });

  const [goals, setGoals] = useState({
    targetVerses: 10,
    targetNotes: 5,
    targetQuestions: 5,
    targetTime: 60 // minutes
  });

  useEffect(() => {
    // Calculate progress from study guide
    const verses = studyGuide.sections.filter(section => section.type === 'verse').length;
    const notes = studyGuide.sections.filter(section =>
      section.type === 'notes' && section.content && section.content.trim().length > 0
    ).length;
    const questions = studyGuide.sections.filter(section =>
      section.type === 'questions' && section.questions && section.questions.some(q => q && q.trim())
    ).length;
    const sections = studyGuide.sections.length;

    setProgress({
      versesRead: verses,
      notesWritten: notes,
      questionsAnswered: questions,
      sectionsCompleted: sections
    });
  }, [studyGuide.sections]);

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const ProgressBar = ({ current, target, label, color = '#667eea' }) => {
    const percentage = getProgressPercentage(current, target);
    return (
      <div className="progress-item">
        <div className="progress-label">
          <span>{label}</span>
          <span className="progress-count">{current}/{target}</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${percentage}%`,
              backgroundColor: color
            }}
          />
        </div>
        <div className="progress-percentage">{Math.round(percentage)}%</div>
      </div>
    );
  };

  return (
    <div className="progress-tracker">
      <div className="progress-header">
        <h3>Study Progress</h3>
        <div className="study-time">
          <span className="time-icon">⏱️</span>
          <span>{formatTime(Math.floor(studyTime / 60))}</span>
        </div>
      </div>

      <div className="progress-goals">
        <h4>Current Goals</h4>
        <div className="goals-grid">
          <div className="goal-item">
            <span className="goal-icon">📖</span>
            <span>{goals.targetVerses} verses</span>
          </div>
          <div className="goal-item">
            <span className="goal-icon">📝</span>
            <span>{goals.targetNotes} notes</span>
          </div>
          <div className="goal-item">
            <span className="goal-icon">❓</span>
            <span>{goals.targetQuestions} questions</span>
          </div>
          <div className="goal-item">
            <span className="goal-icon">⏰</span>
            <span>{formatTime(goals.targetTime)}</span>
          </div>
        </div>
      </div>

      <div className="progress-bars">
        <ProgressBar
          current={progress.versesRead}
          target={goals.targetVerses}
          label="Verses Read"
          color="#667eea"
        />
        <ProgressBar
          current={progress.notesWritten}
          target={goals.targetNotes}
          label="Notes Written"
          color="#27ae60"
        />
        <ProgressBar
          current={progress.questionsAnswered}
          target={goals.targetQuestions}
          label="Questions Answered"
          color="#e74c3c"
        />
        <ProgressBar
          current={Math.floor(studyTime / 60)}
          target={goals.targetTime}
          label="Study Time"
          color="#f39c12"
        />
      </div>

      <div className="progress-summary">
        <h4>Study Summary</h4>
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Sections:</span>
            <span className="stat-value">{progress.sectionsCompleted}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Study Streak:</span>
            <span className="stat-value">1 day</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Completion Rate:</span>
            <span className="stat-value">
              {Math.round(((progress.versesRead + progress.notesWritten + progress.questionsAnswered) /
                (goals.targetVerses + goals.targetNotes + goals.targetQuestions)) * 100)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProgressTracker;